import {
  createElement,
  Fragment,
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';

/**
 * Places where one module shows a control of another without importing it, e.g. the chat's
 * "Nhắn tin" button on an employee's profile. Each slot has fixed props.
 */
export interface SlotProps {
  /** Actions on an employee's profile (the person's Portal account, if any). */
  'employee.actions': { userId: string };
  /** Actions in the header of a project page. */
  'project.actions': { projectId: string };
}
export type SlotName = keyof SlotProps;
export type SlotLoader<N extends SlotName = SlotName> = () => Promise<{
  default: ComponentType<SlotProps[N]>;
}>;

/** Props are checked at `Slot`; inside, components are stored without them. */
type AnySlot = ComponentType<Record<string, unknown>>;

const loaders = new Map<SlotName, SlotLoader[]>();
const components = new Map<SlotLoader, LazyExoticComponent<AnySlot>>();

/** Called once by the shell with every web module, like the widgets. */
export function registerSlots(
  modules: Array<{ slots?: { [N in SlotName]?: SlotLoader<N> } }>,
): void {
  for (const module of modules) {
    for (const [name, loader] of Object.entries(module.slots ?? {})) {
      const list = loaders.get(name as SlotName) ?? [];
      list.push(loader as SlotLoader);
      loaders.set(name as SlotName, list);
    }
  }
}

/** Renders what the modules put in a slot (nothing when none does). */
export function Slot<N extends SlotName>({ name, ...props }: { name: N } & SlotProps[N]) {
  const list = loaders.get(name) ?? [];
  if (list.length === 0) return null;
  return createElement(
    Suspense,
    { fallback: null },
    ...list.map((loader, i) => {
      const component =
        components.get(loader) ?? lazy(loader as () => Promise<{ default: AnySlot }>);
      components.set(loader, component);
      return createElement(Fragment, { key: i }, createElement(component, props));
    }),
  );
}
